import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Collapse,
  IconButton, Paper, Tooltip, Typography,
} from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  PriceSuggestion, ItemIntelligence, ItemAlert,
  fetchSuggestedPrice, fetchItemIntelligence, fetchItemAlerts,
} from "./vendorIntelligenceApi";

/* ── Theme ──────────────────────────────────────────────────────────────── */
const T = {
  navy: "#0d1b2a", gold: "#c9a96e", goldLight: "#f5eed9",
  white: "#fff", cream: "#faf8f4", bg: "#f7f5f1",
  slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
  green: "#2d6a35", greenBg: "#edf7ee",
  red: "#9b2c2c", redBg: "#fef2f2",
  blue: "#1e40af", blueBg: "#eff6ff",
  orange: "#c2410c", orangeBg: "#fff7ed",
};

const fmtCAD = (n: number) =>
  `$${n.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CONFIDENCE_STYLES: Record<string, { bg: string; tx: string; label: string }> = {
  high:   { bg: T.greenBg, tx: T.green, label: "High" },
  medium: { bg: T.goldLight, tx: "#8b6914", label: "Medium" },
  low:    { bg: T.orangeBg, tx: T.orange, label: "Low" },
  none:   { bg: "#f1f5f9", tx: T.slateLt, label: "None" },
};

const ALERT_ICONS: Record<string, React.ReactNode> = {
  danger:  <ErrorOutlineIcon sx={{ fontSize: 16, color: T.red }} />,
  warning: <WarningAmberIcon sx={{ fontSize: 16, color: T.orange }} />,
  info:    <InfoOutlinedIcon sx={{ fontSize: 16, color: T.blue }} />,
};

const ALERT_COLORS: Record<string, { bg: string; tx: string; border: string }> = {
  danger:  { bg: T.redBg, tx: T.red, border: "#fca5a5" },
  warning: { bg: T.orangeBg, tx: T.orange, border: "#fed7aa" },
  info:    { bg: T.blueBg, tx: T.blue, border: "#bfdbfe" },
};

/* ═══════════════════════════════════════════════════════════════════════════
   PRICING SUGGESTION PANEL
   Shows smart pricing suggestions and historical intelligence
   ═══════════════════════════════════════════════════════════════════════════ */
interface Props {
  itemId: number;
  vendorId: number;
  eventType?: string;
  guestCount?: number;
  pricingType?: string;
  durationHours?: number;
  currentDccPrice?: number;
  onApplyPrice?: (price: number) => void;
}

const PricingSuggestionPanel: React.FC<Props> = ({
  itemId, vendorId, eventType, guestCount, pricingType, durationHours,
  currentDccPrice, onApplyPrice,
}) => {
  const [suggestion, setSuggestion] = useState<PriceSuggestion | null>(null);
  const [intelligence, setIntelligence] = useState<ItemIntelligence | null>(null);
  const [alerts, setAlerts] = useState<ItemAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sug, intel, al] = await Promise.all([
        fetchSuggestedPrice({ item_id: itemId, vendor_id: vendorId, event_type: eventType, guest_count: guestCount, pricing_type: pricingType, duration_hours: durationHours }),
        fetchItemIntelligence(itemId, vendorId),
        fetchItemAlerts(itemId, vendorId, currentDccPrice),
      ]);
      setSuggestion(sug);
      setIntelligence(intel);
      setAlerts(al);
    } catch (e) { console.error("Pricing suggestion error:", e); }
    setLoading(false);
  }, [itemId, vendorId, eventType, guestCount, pricingType, durationHours, currentDccPrice]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Paper sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${T.line}`, textAlign: "center" }}>
        <CircularProgress size={20} sx={{ color: T.gold }} />
        <Typography sx={{ fontSize: 11, color: T.slate, mt: 0.5 }}>Analyzing pricing history...</Typography>
      </Paper>
    );
  }

  const conf = suggestion ? CONFIDENCE_STYLES[suggestion.confidence] : CONFIDENCE_STYLES.none;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* ── Alerts ───────────────────────────────────────────────────────── */}
      {alerts.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
          {alerts.map((alert, i) => {
            const ac = ALERT_COLORS[alert.type] || ALERT_COLORS.info;
            return (
              <Box key={i} sx={{
                display: "flex", alignItems: "center", gap: 1, p: 1.2,
                borderRadius: 2, bgcolor: ac.bg, border: `1px solid ${ac.border}`,
              }}>
                {ALERT_ICONS[alert.type]}
                <Typography sx={{ fontSize: 11, color: ac.tx, fontWeight: 500 }}>{alert.message}</Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* ── Price Suggestion Card ────────────────────────────────────────── */}
      {suggestion && suggestion.suggested_price > 0 && (
        <Paper sx={{
          borderRadius: 3, border: `1px solid ${T.line}`,
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)", overflow: "hidden",
        }}>
          <Box sx={{
            px: 2, py: 1.2, bgcolor: T.navy,
            display: "flex", alignItems: "center", gap: 1,
          }}>
            <LightbulbOutlinedIcon sx={{ fontSize: 14, color: T.gold }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.white }}>
              Smart Pricing Suggestion
            </Typography>
          </Box>

          <Box sx={{ p: 2 }}>
            {/* Main suggestion */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: 10, color: T.slateLt, fontWeight: 600, textTransform: "uppercase" }}>
                  Suggested Price
                </Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 800, color: T.navy, lineHeight: 1.1 }}>
                  {fmtCAD(suggestion.suggested_price)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Chip label={`${conf.label} Confidence`} size="small" sx={{
                  height: 22, fontSize: 10, fontWeight: 700, bgcolor: conf.bg, color: conf.tx,
                }} />
                <Typography sx={{ fontSize: 10, color: T.slateLt, mt: 0.5 }}>
                  Based on {suggestion.sample_size} event{suggestion.sample_size !== 1 ? "s" : ""}
                </Typography>
              </Box>
            </Box>

            {/* Reason */}
            <Typography sx={{ fontSize: 12, color: T.slate, mb: 1.5, lineHeight: 1.5 }}>
              {suggestion.reason}
            </Typography>

            {/* Margin insight */}
            {suggestion.avg_margin > 0 && (
              <Box sx={{
                display: "flex", alignItems: "center", gap: 1, p: 1,
                borderRadius: 2, bgcolor: T.cream, mb: 1.5,
              }}>
                <TrendingUpIcon sx={{ fontSize: 14, color: T.green }} />
                <Typography sx={{ fontSize: 11, color: T.slate }}>
                  Typical margin for this item is <strong>{suggestion.avg_margin}%</strong>
                </Typography>
              </Box>
            )}

            {/* Price range */}
            <Box sx={{ display: "flex", gap: 2, mb: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: 10, color: T.slateLt }}>Min Seen</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy }}>{fmtCAD(suggestion.min_seen_price)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, color: T.slateLt }}>Avg</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.blue }}>{fmtCAD(suggestion.avg_price)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, color: T.slateLt }}>Max Seen</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.green }}>{fmtCAD(suggestion.max_seen_price)}</Typography>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button size="small" variant="contained" startIcon={<CheckCircleOutlineIcon />}
                onClick={() => onApplyPrice?.(suggestion.suggested_price)}
                sx={{
                  bgcolor: T.navy, color: T.white, fontSize: 11, fontWeight: 600,
                  borderRadius: 2, textTransform: "none",
                  "&:hover": { bgcolor: T.gold, color: T.navy },
                }}>
                Apply Suggestion
              </Button>
              <Button size="small" variant="outlined"
                onClick={() => onApplyPrice?.(suggestion.avg_price)}
                sx={{
                  borderColor: T.line, color: T.slate, fontSize: 11, fontWeight: 600,
                  borderRadius: 2, textTransform: "none",
                }}>
                Apply Avg ({fmtCAD(suggestion.avg_price)})
              </Button>
              <Button size="small" variant="outlined"
                onClick={() => onApplyPrice?.(suggestion.max_seen_price)}
                sx={{
                  borderColor: T.line, color: T.green, fontSize: 11, fontWeight: 600,
                  borderRadius: 2, textTransform: "none",
                }}>
                Apply Highest ({fmtCAD(suggestion.max_seen_price)})
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* ── Past Event Intelligence ──────────────────────────────────────── */}
      {intelligence && intelligence.total_uses > 0 && (
        <Paper sx={{
          borderRadius: 3, border: `1px solid ${T.line}`, boxShadow: "none", overflow: "hidden",
        }}>
          <Box sx={{
            px: 2, py: 1.2, display: "flex", alignItems: "center", justifyContent: "space-between",
            bgcolor: T.cream, cursor: "pointer",
          }} onClick={() => setShowHistory(p => !p)}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <HistoryOutlinedIcon sx={{ fontSize: 14, color: T.navy }} />
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.navy }}>
                Past Event Intelligence
              </Typography>
              <Chip label={`${intelligence.total_uses} uses`} size="small" sx={{
                height: 18, fontSize: 9, fontWeight: 600, bgcolor: T.blueBg, color: T.blue,
              }} />
            </Box>
            <IconButton size="small">
              {showHistory
                ? <ExpandLessIcon sx={{ fontSize: 14, color: T.slate }} />
                : <ExpandMoreIcon sx={{ fontSize: 14, color: T.slate }} />}
            </IconButton>
          </Box>

          <Collapse in={showHistory}>
            <Box sx={{ p: 2, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: 10, color: T.slateLt }}>Avg Cost</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.navy }}>{fmtCAD(intelligence.avg_cost)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, color: T.slateLt }}>Avg Selling Price</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.blue }}>{fmtCAD(intelligence.avg_selling_price)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, color: T.slateLt }}>Avg Margin</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: intelligence.avg_margin >= 30 ? T.green : T.red }}>
                  {intelligence.avg_margin}%
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, color: T.slateLt }}>Price Range</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy }}>
                  {fmtCAD(intelligence.min_rate)} - {fmtCAD(intelligence.max_rate)}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, color: T.slateLt }}>Avg Guest Count</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy }}>{intelligence.avg_guest_count}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, color: T.slateLt }}>Last Used</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy }}>
                  {intelligence.last_used || "N/A"}
                </Typography>
              </Box>
            </Box>

            {(intelligence.event_type_breakdown.length > 0 || intelligence.pricing_type_breakdown.length > 0) && (
              <Box sx={{ px: 2, pb: 2, display: "flex", gap: 2 }}>
                {intelligence.event_type_breakdown.length > 0 && (
                  <Box>
                    <Typography sx={{ fontSize: 10, color: T.slateLt, mb: 0.5 }}>Event Types</Typography>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {intelligence.event_type_breakdown.map(e => (
                        <Chip key={e.type} label={`${e.type || "N/A"} (${e.count})`} size="small" sx={{
                          height: 20, fontSize: 9, fontWeight: 600, bgcolor: T.blueBg, color: T.blue,
                        }} />
                      ))}
                    </Box>
                  </Box>
                )}
                {intelligence.pricing_type_breakdown.length > 0 && (
                  <Box>
                    <Typography sx={{ fontSize: 10, color: T.slateLt, mb: 0.5 }}>Pricing Types</Typography>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {intelligence.pricing_type_breakdown.map(p => (
                        <Chip key={p.type} label={`${p.type} (${p.count})`} size="small" sx={{
                          height: 20, fontSize: 9, fontWeight: 600, bgcolor: T.goldLight, color: "#8b6914",
                        }} />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Collapse>
        </Paper>
      )}
    </Box>
  );
};

export default PricingSuggestionPanel;
